/**
 * Persist adapter for host-owned draft data, plus the per-flow completion-lock floor.
 *
 * Draft keys are `flow-data-{namespace}` in localStorage. Existing namespaces are frozen:
 * `kit-registration`, `sample-collection` — renaming strands live patient drafts.
 *
 * Completion-lock keys are `flow-completion-lock-{flowId}`. Missing is not the same as
 * `-1`: `-1` means the floor was explicitly lifted; missing may seed from draft progress.
 */
export const FLOW_DATA_STORAGE_PREFIX = 'flow-data-'
export const FLOW_COMPLETION_LOCK_STORAGE_PREFIX = 'flow-completion-lock-'

export type PersistAdapter = {
  read: (namespace: string) => unknown
  write: (namespace: string, value: unknown) => void
  clear: (namespace: string) => void
  clearAll: () => void
}

function keyForNamespace(namespace: string): string | null {
  if (!namespace) return null
  const key = `${FLOW_DATA_STORAGE_PREFIX}${namespace}`
  return key.startsWith(FLOW_DATA_STORAGE_PREFIX) ? key : null
}

function completionLockKeyForFlowId(flowId: string): string | null {
  if (!flowId) return null
  const key = `${FLOW_COMPLETION_LOCK_STORAGE_PREFIX}${flowId}`
  return key.startsWith(FLOW_COMPLETION_LOCK_STORAGE_PREFIX) ? key : null
}

/**
 * Integer lock floor for `flowId`, or `null` when nothing has been written (or storage
 * is unavailable / corrupt). `null` is not `-1`.
 */
export function readCompletionLockLevel(flowId: string): number | null {
  if (typeof window === 'undefined') return null

  const key = completionLockKeyForFlowId(flowId)
  if (!key) return null

  try {
    const raw = localStorage.getItem(key)
    if (raw === null) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'number' || !Number.isInteger(parsed)) return null
    return parsed
  } catch {
    return null
  }
}

export function writeCompletionLockLevel(flowId: string, level: number): void {
  if (typeof window === 'undefined') return

  const key = completionLockKeyForFlowId(flowId)
  if (!key) return

  try {
    localStorage.setItem(key, JSON.stringify(level))
  } catch (error) {
    console.warn('Error persisting completion lock:', error)
  }
}

export const persistAdapter: PersistAdapter = {
  read(namespace) {
    if (typeof window === 'undefined') return null

    const key = keyForNamespace(namespace)
    if (!key) return null

    try {
      const stored = localStorage.getItem(key)
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  },

  write(namespace, value) {
    if (typeof window === 'undefined') return

    const key = keyForNamespace(namespace)
    if (!key) return

    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch (error) {
      console.warn('Error persisting flow data:', error)
    }
  },

  clear(namespace) {
    if (typeof window === 'undefined') return

    const key = keyForNamespace(namespace)
    if (!key) return

    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.warn('Error clearing flow data:', error)
    }
  },

  clearAll() {
    if (typeof window === 'undefined') return

    try {
      Object.keys(localStorage)
        .filter(
          (key) =>
            key.startsWith(FLOW_DATA_STORAGE_PREFIX) ||
            key.startsWith(FLOW_COMPLETION_LOCK_STORAGE_PREFIX),
        )
        .forEach((key) => {
          try {
            localStorage.removeItem(key)
          } catch (error) {
            console.warn('Error clearing flow data:', error)
          }
        })
    } catch (error) {
      console.warn('Error clearing flow data:', error)
    }
  },
}

/**
 * `currentStepKey` from a host draft blob. Ignores other shapes (e.g. `currentKey`).
 * Namespace matches `flowConfig.id` for production hosts.
 */
export function readPersistedCurrentStepKey(namespace: string): string | null {
  const stored = persistAdapter.read(namespace)
  if (!stored || typeof stored !== 'object') return null
  const currentStepKey = (stored as { currentStepKey?: unknown }).currentStepKey
  return typeof currentStepKey === 'string' ? currentStepKey : null
}
